import { z } from "zod";

import type { DiagnosisItem } from "@/src/models/documents/referral";

export function normalizeICD10Code(code: string | null | undefined): string {
  if (!code) return "";

  return code.replace(/\./g, "").replace(/^1/, "I"); // Replace leading 1 with I to match the database and remove dots
}

export function formatICD10CodeForDisplay(code: string | null | undefined): string {
  const normalized = normalizeICD10Code(code);

  if (normalized.length > 3) {
    return `${normalized.slice(0, 3)}.${normalized.slice(3)}`;
  }

  return normalized;
}

export const icd10Schema = z.object({
  id: z.string().uuid(),
  code: z
    .string()
    .min(3, "ICD-10 code is required")
    .max(8, "ICD-10 code must be 8 characters or less")
    .transform((val) => normalizeICD10Code(val)),
  description: z.string().min(1, "Description is required"),
  category: z.string().nullable().optional(),
  // Headers (e.g. N18) are for grouping only; only billable codes belong on a referral.
  isBillable: z.boolean().optional(),
  validStart: z.string().nullable().optional(),
  validEnd: z.string().nullable().optional(),
  searchText: z.string().optional(),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
  deletedAt: z.date().nullable().optional(),
}).describe(`
  ICD-10 diagnosis code for lookup purposes only.
  
  ICD-10 codes are standardized medical codes that should only be looked up,
  not created or modified. This schema is used for validation during lookup.
  
  The searchText field contains denormalized searchable text combining code,
  description, and category for efficient full-text search.
`);

export type ICD10 = z.infer<typeof icd10Schema>;

export function getICD10DisplayName(icd10: ICD10): string {
  const formattedCode = formatICD10CodeForDisplay(icd10.code);

  return `${formattedCode} - ${icd10.description}`;
}

export function getICD10ShortDisplayName(icd10: ICD10): string {
  return formatICD10CodeForDisplay(icd10.code);
}

// VLM extracts diagnosis items in this format.
// We need to map DiagnosisItems to ICD10 to match, then map back to DiagnosisItems to display+save.
export function diagnosisItemToICD10(diagnosisItem: DiagnosisItem): ICD10 {
  return {
    id: "",
    code: normalizeICD10Code(diagnosisItem.diagnosisCode ?? ""),
    description: diagnosisItem.diagnosisText ?? "",
    category: diagnosisItem.diagnosisCodeType,
  };
}

export function icd10ToDiagnosisItem(icd10: ICD10): DiagnosisItem {
  return {
    diagnosisCode: icd10.code,
    diagnosisText: icd10.description,
    diagnosisCodeType: "ICD-10",
  };
}
